import axios from 'axios';
import { UserProfile } from '../types/user';
import { API_ENDPOINTS } from '../utils/apiConfig';

// Einheitliche Endpoints für alle API-Aufrufe in der Schulungs-App.
const API_URL = API_ENDPOINTS.api;
const TOKEN_LOGIN_URL = API_ENDPOINTS.tokenLogin;
const SSO_LOGIN_URL = API_ENDPOINTS.ssoLogin;

// Axios Interceptor: Fügt automatisch Authorization Header zu jedem Request hinzu
axios.interceptors.request.use(
  (config) => {
    // Token aus localStorage holen
    const token = localStorage.getItem('auth_token');
    
    // Wenn Token vorhanden und noch kein Authorization Header gesetzt → hinzufügen
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Wenn ein Token existiert, setze es als Default-Header (z.B. nach Page-Reload)
if (typeof window !== 'undefined') {
  const existingToken = localStorage.getItem('auth_token');
  if (existingToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
  }
}

interface LoginResponse {
  token: string;
  role?: string;
  roles?: string[];
  error?: string;
}

interface SSOLoginResponse {
  success: boolean;
  token: string;
  role: string;
  roles?: string[];
  user: {
    id: number;
    email: string;
    alias: string;
  };
  message: string;
  is_new_user?: boolean;
  error?: string;
}

const createApiError = (fallbackError: string, fallbackMessage?: string, details?: unknown): Error => {
  const err = new Error(fallbackMessage || fallbackError);
  err.name = 'AuthServiceError';
  (err as any).error = fallbackError;
  if (details) {
    (err as any).details = details;
  }
  return err;
};

const parseApiError = (data: any, fallbackError: string, fallbackMessage?: string): Error => {
  if (data && typeof data === 'object') {
    const serverMsg = (typeof data.error === 'string' && data.error) || (typeof data.message === 'string' && data.message) || undefined;
    const text = serverMsg || fallbackMessage || fallbackError;
    return createApiError(text, text, data);
  }
  return createApiError(fallbackError, fallbackMessage || fallbackError);
};

export const authService = {
  startOAuthLogin(returnTo?: string): void {
    const fallbackReturnTo = `${window.location.origin}/#/login`;
    const targetReturnTo = (returnTo && returnTo.trim().length > 0) ? returnTo : fallbackReturnTo;
    const url = `${SSO_LOGIN_URL}?mode=oauth_start&return_to=${encodeURIComponent(targetReturnTo)}`;
    window.location.assign(url);
  },

  /**
   * SSO Login - Authentifiziert über externe Session
   * Erstellt automatisch Account falls User neu ist
   */
  async ssoLogin(): Promise<SSOLoginResponse> {
    try {
      const response = await axios.post<SSOLoginResponse>(SSO_LOGIN_URL, {}, {
        withCredentials: true // Wichtig: Sendet Cookies/Session mit
      });
      
      if (response.data.success && response.data.token) {
        // Token & Rolle speichern
        this.storeToken(response.data.token);
        this.storeRole(response.data.role);
        if (Array.isArray(response.data.roles)) {
          this.storeRoles(response.data.roles);
        }
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw createApiError('Nicht authentifiziert', 'Keine gültige Session. Bitte melden Sie sich im Portal an.', error.response?.data);
      }
      throw parseApiError(error.response?.data, 'SSO-Login fehlgeschlagen', error.message);
    }
  },

  /**
   * Standard Login mit Email/Passwort
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    // Token-basierter Login über separates Endpoint (wie in Android)
    try {
      const response = await axios.post(TOKEN_LOGIN_URL, { email, password });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler', error.message);
    }
  },

  async register(email: string, password: string, alias?: string): Promise<{ success?: boolean; message?: string; error?: string }> {
    try {
      const response = await axios.post(API_URL, {
        action: 'createUser',
        email,
        password,
        alias,
        role: 'user'
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler', error.message);
    }
  },

  async getProfile(token: string): Promise<UserProfile> {
    try {
      const response = await axios.get<UserProfile>(`${API_URL}?action=profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw createApiError('Unauthorized', 'Token ungültig oder Session abgelaufen.', error.response?.data);
      }
      throw parseApiError(error.response?.data, 'Profilabruf fehlgeschlagen', error.message);
    }
  },

  async requestPasswordReset(email: string): Promise<{ message?: string; error?: string }> {
    try {
      const response = await axios.post(API_URL, { action: 'requestPasswordReset', email });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) return error.response.data;
      return { error: 'Netzwerkfehler beim Passwort-Reset.' };
    }
  },

    async changePassword(token: string, newPassword: string): Promise<{ success?: boolean; message?: string; error?: string }> {
      try {
        const response = await axios.post(API_URL, {
          action: 'setNewPasswordByToken',
          new_password: newPassword
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
      } catch (error: any) {
        throw parseApiError(error.response?.data, 'Netzwerkfehler beim Passwort ändern.', error.message);
      }
    },

  async getAllUsers(token: string): Promise<any[]> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'getAllUsers' },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Laden der Benutzerliste.', error.message);
    }
  },

  async getRolePointGoals(token: string): Promise<{ success?: boolean; roleMinPoints?: Record<string, number>; error?: string }> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'getRolePointGoals' },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Laden der Rollenziele.', error.message);
    }
  },

  async updateRolePointGoals(token: string, roleMinPoints: Record<string, number>): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, {
        action: 'updateRolePointGoals',
        roleMinPoints
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Speichern der Rollenziele.', error.message);
    }
  },

  async triggerWeeklyRoleGoalNotifications(token: string, force: boolean = true): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, {
        action: 'sendWeeklyRoleGoalNotifications',
        force
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Auslösen der Wochenziel-Erinnerungen.', error.message);
    }
  },

  async getRolesLegend(token: string): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'getRolesLegend' },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Laden der Rollen-Legende.', error.message);
    }
  },

  async updateRolesLegend(token: string, legend: any): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, {
        action: 'updateRolesLegend',
        legend
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Speichern der Rollen-Legende.', error.message);
    }
  },

  async updateUser(token: string, data: Record<string, any>): Promise<any> {
    try {
      const body = { ...data, action: 'updateUser' };
      const response = await axios.post(API_URL, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Aktualisieren des Users.', error.message);
    }
  },

  async adminUpdateUser(token: string, userId: string | number, data: Record<string, any>): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const body = { ...data, action: 'adminUpdateUser', userId };
      const response = await axios.post(API_URL, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Aktualisieren eines Benutzers.', error.message);
    }
  },

  async updateUserActiveStatus(token: string, email: string, isActive: boolean): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: {
          action: 'updateUserActiveStatus',
          email,
          active: isActive
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Ändern des Aktiv-Status.', error.message);
    }
  },

  async deleteUser(token: string, userId: string | number): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, { action: 'deleteUser', userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Löschen des Benutzers.', error.message);
    }
  },

  async adminUpdateUserFieldsGet(token: string, params: { userId?: string | number; email?: string; alias?: string; role?: string; roles?: string[] | string; event?: string | null; info?: string | null; weeklyGoalNotifyEnabled?: boolean }): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'adminUpdateUserFields', ...params },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Aktualisieren des Benutzers (GET).', error.message);
    }
  },

  async deleteUserByIdGet(token: string, userId: string | number): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'deleteUserById', userId },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Löschen des Benutzers (GET).', error.message);
    }
  },

  async adminSendWhatsAppMessage(token: string, userId: string | number, message: string): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, {
        action: 'adminSendWhatsAppMessage',
        userId,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Senden der WhatsApp-Nachricht.', error.message);
    }
  },

  async adminCreateUser(token: string, payload: { email: string; alias: string; role?: string; roles?: string[]; active?: boolean; event?: string | null; info?: string | null; password?: string; weeklyGoalNotifyEnabled?: boolean }): Promise<any> {
    if (!token) throw createApiError('Unauthorized', 'Keine Authentifizierung.');
    try {
      const response = await axios.post(API_URL, {
        action: 'adminCreateUser',
        ...payload
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      throw parseApiError(error.response?.data, 'Netzwerkfehler beim Anlegen des Benutzers.', error.message);
    }
  },

  storeToken(token: string): void {
    localStorage.setItem('auth_token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },
  removeToken(): void {
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common.Authorization;
  },

  storeRole(role: string): void {
    localStorage.setItem('auth_role', role);
  },
  storeRoles(roles: string[]): void {
    localStorage.setItem('auth_roles', JSON.stringify(roles));
  },
  getRole(): string | null {
    return localStorage.getItem('auth_role');
  },
  getRoles(): string[] | null {
    const raw = localStorage.getItem('auth_roles');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },
  removeRole(): void {
    localStorage.removeItem('auth_role');
  },
  removeRoles(): void {
    localStorage.removeItem('auth_roles');
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
};

export default authService;